import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { patientEntityService } from '@/api/services/patientEntityService'
import { QUERY_KEYS } from '@/config/api'
import { createApiRetryHandler } from '@/utils/apiRetry'

const normalizePhoneForPatientLookup = (value?: string | null) =>
  String(value || '').replace(/\D/g, '')

export const PATIENT_ENTITY_QUERY_KEYS = {
  all: QUERY_KEYS.patientEntities.all,
  contacts: (contactNumbers: string[]) =>
    [...QUERY_KEYS.patientEntities.all, 'contacts', contactNumbers] as const,
}

export const usePatientEntityLookups = (
  contactNumbers: Array<string | null | undefined>,
  enabled = true
) => {
  const uniqueContactNumbers = useMemo(
    () =>
      Array.from(
        new Map(
          contactNumbers
            .map(value => String(value || '').trim())
            .filter(value => normalizePhoneForPatientLookup(value).length >= 7)
            .map(value => [normalizePhoneForPatientLookup(value), value])
        ).values()
      )
        .sort()
        .slice(0, 10),
    [contactNumbers]
  )

  return useQuery({
    queryKey: PATIENT_ENTITY_QUERY_KEYS.contacts(uniqueContactNumbers),
    queryFn: () =>
      patientEntityService.lookupByContactNumbers(uniqueContactNumbers),
    enabled: enabled && uniqueContactNumbers.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: createApiRetryHandler(1),
  })
}
